import { css, keyframes } from 'goober';
import { isEmpty, isFunction } from 'mc-ui-utils';
import React, { Component } from 'react';
import BubbleEventFactory from '../../analytics/EventFactory';
import constants from '../../constants';
import { INTERACTION_EXP_DELAY } from '../../constants/interaction';
import State from '../../state';
import colors from '../../styles/colors';
import font from '../../styles/font';
import { transparentize } from '../../styles/mixins';
import { addHighlightsbridge, resetHighlightBridge, } from '../../utils/bridgehelper';
import { d, getViewPortDimensions, hasNativeVideo, hasYoutubeVideo, isWebpeek, } from '../../utils/common';
import { getCTA, isInRenderThreshold } from '../../utils/data';
import { getInteractionExperiment } from '../../utils/interactionexputils';
import { checkCardVisible, getSlideClassName, isBeingSwiped, } from '../../utils/slider';
import BubbleDetails from '../BubbleDetails';
import CoachMarkContainer from '../CoachMarkContainer';
import GlanceHighlights from '../GlanceHighlights';
import GlanceLogo from '../GlanceLogo';
import PlayBtn from '../Playbtn';
import ShareTray from '../ShareTray';
import SwitchLanguage from '../SwitchLanguage';
import CTActions from './CTActions';
import InteractionCTActions from './InteractionCTActions';
import JioBanner from './JioBanner';
import Navigator from './Navigator';
import OfflineBadge from './OfflineBadge';
import Stories from './Stories';
const { vh } = getViewPortDimensions();
const ContentAnimation = keyframes `
  0% {
    transform: translate3d(0%, 0, 1px);
  }
  10% {
    transform: translate3d(0%, 0, 1px);
  }
  75% {
    transform: translate3d(-22px, 0, 1px);
  }
  100% {
    transform: translate3d(-22px, 0, 1px);
  }
`;
const fadeIn = keyframes `
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
const Container = css `
  position: absolute;
  background-color: ${colors.black};
  width: 100%;
  top: 0;
  left: 0;
  min-height: 100vh;
  height: ${vh};
  overflow: hidden;
  transform: translate3d(0%, 0, -1px);
  opacity: 0;
  will-change: transform, opacity;
  user-select: none;
  touch-action: none;
  -webkit-user-drag: none;
  transition-timing-function: linear;
  &.current {
    transform: translate3d(0%, 0, 1px);
    opacity: 1;
    transition: transform ${constants.TRANSITION_DURATION};
    &.has-coachmark {
      animation: ${ContentAnimation} 2s ease-in infinite;
    }
  }
  &.prev2,
  &.prev {
    transform: translate3d(-100%, 0, 2px);
  }
  &.prev,
  &.next {
    opacity: 1;
    transition: transform ${constants.TRANSITION_DURATION};
    animation: ${fadeIn} 0.5s ease-in-out forwards;
  }
  &.next {
    transform: translate3d(0%, 0, 0);
  }
`;
const Attribution = css `
  position: absolute;
  right: 20px;
  bottom: ${parseInt(d.glanceBottomGap, 10) + 20}px;
  font-size: ${font.fontSmall};
  color: ${transparentize(0.5, colors.white)};
`;
const StoryContainer = css `
  position: absolute;
  top: calc(${d.glanceTopGap} + ${d.sideGap});
  left: 0;
  right: 0;
`;
const BubbleContainer = css `
  position: absolute;
  top: calc(${d.glanceTopGap} + ${d.sideGap});
  left: 0;
  right: auto;
  &.video {
    right: 0;
  }
`;
const playBtnCss = css `
  width: 84px;
  height: 84px;
  position: absolute;
  left: calc(50% - 42px);
  top: calc(50% - 20px);
  background-position: center;
  background-size: cover;
`;
class Glance extends Component {
    constructor(props) {
        super(props);
        this.tapEventFired = false;
        this.glanceIndexIdMap = {};
        this.elementsTobeHidden = [];
        this.webpeekLoadedPending = {};
        this.addbridges = () => {
            if (State.isSdk)
                addHighlightsbridge(this);
        };
        this.onNavigate = (e) => {
            const step = parseInt(e.target.dataset.step, 10);
            const { highlightIndex } = this.state;
            const { currentGlanceIndex, setSlideshowIndex, setStoryIdInPWA, bubble: { glances }, } = this.props;
            const currentGlanceId = glances[highlightIndex].glanceId;
            const totalHighlights = glances.length;
            const newIndex = highlightIndex + step;
            const isFirstCard = newIndex === -1;
            const { totalGlances } = State;
            const isInThreshold = currentGlanceIndex < totalGlances - 1;
            const isEndOfStory = newIndex === totalHighlights;
            if (isFirstCard || (isEndOfStory && isInThreshold)) {
                if (isFirstCard && currentGlanceIndex > 0) {
                    /***
                     * Is first glance card, and need to move to previous bubble
                     * fire impression for current glance and bubble
                     */
                    setSlideshowIndex(currentGlanceIndex - 1);
                    this.tapEventFired = true;
                    State.lastPageChangeSource = "TapLeft" /* TAP_LEFT */;
                    this.bubbleEvent.setImpressionEnd(currentGlanceId, "TapLeft" /* TAP_LEFT */, this.currentGlanceHasVideo());
                }
                if (isEndOfStory) {
                    /***
                     * Is last glance card, and need to move to next bubble
                     * fire impression for current glance and bubble
                     */
                    setSlideshowIndex(currentGlanceIndex + 1);
                    this.tapEventFired = true;
                    State.lastPageChangeSource = "TapRight" /* TAP_RIGHT */;
                    this.bubbleEvent.setImpressionEnd(currentGlanceId, "TapRight" /* TAP_RIGHT */, this.currentGlanceHasVideo());
                }
            }
            else if (newIndex >= 0 && newIndex < totalHighlights) {
                /***
                 * Same bubble, change glance card
                 */
                const source = newIndex > highlightIndex
                    ? "TapRight" /* TAP_RIGHT */
                    : "TapLeft" /* TAP_LEFT */;
                //sending glanceID to glance webapp-SSR to update URL in the reactivation flow.
                if (!State.isSdk && isFunction(setStoryIdInPWA)) {
                    setStoryIdInPWA(glances[newIndex].glanceId);
                }
                this.setHighlightIndex(newIndex, source);
            }
            if (isFunction(e.preventDefault)) {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        this.getCurrentGlanceId = () => {
            const { highlightIndex } = this.state;
            return this.glanceIndexIdMap[highlightIndex];
        };
        this.currentGlanceHasVideo = () => {
            const { highlightIndex } = this.state;
            const { bubble: { glances }, } = this.props;
            const currentGlance = glances[highlightIndex];
            return hasYoutubeVideo(currentGlance) || hasNativeVideo(currentGlance);
        };
        this.onHoldGlance = () => {
            if (isBeingSwiped()) {
                this.setState({
                    isAutoPlayPaused: true,
                });
            }
            else {
                this.bubbleEvent.setGlanceIsHolding(this.getCurrentGlanceId());
                this.setState({
                    isHolding: true,
                });
            }
        };
        this.onUnHoldGlance = () => {
            if (this.state.isHolding) {
                this.bubbleEvent.setGlanceFinishedHolding(this.getCurrentGlanceId());
            }
            this.setState({
                isHolding: false,
                isAutoPlayPaused: false,
            });
        };
        this.setHighlightIndex = (newHighlightIndex, source = "AutoNext" /* AUTO */) => {
            State.lastPageChangeSource = "AutoNext" /* AUTO */;
            const currentGlanceId = this.getCurrentGlanceId();
            this.setState({
                highlightIndex: newHighlightIndex,
            }, () => {
                this.bubbleEvent.setGlanceShown(this.getCurrentGlanceId(), source);
                this.addbridges();
            });
            this.bubbleEvent.setGlanceImpressionEnd(currentGlanceId, source, this.currentGlanceHasVideo());
            const { index } = this.props;
            State.setStoryStatus(index, newHighlightIndex);
            this.elementsTobeHidden = [];
        };
        this.toggleAutoPlay = () => {
            const { isAutoPlayPaused } = this.state;
            this.setState({
                isAutoPlayPaused: !isAutoPlayPaused,
            });
        };
        this.loadYoutube = (e) => {
            const element = e.currentTarget;
            // pause autoplay
            const { id } = element.dataset;
            if (!isEmpty(id)) {
                const { youtubeLoaded } = this.state;
                this.setState({
                    youtubeLoaded: Object.assign(Object.assign({}, youtubeLoaded), { [id]: true }),
                    isAutoPlayPaused: true,
                });
            }
        };
        this.startPendingAutoPlay = () => {
            this.setState({
                isAutoPlayPaused: false,
            });
        };
        this.closeDrawerMenu = () => {
            State.isSlidePrevented = false;
            this.setState({
                isAutoPlayPaused: false,
                isDrawerOpen: false,
            });
        };
        /**
         * toggleDrawerMenu gets called on click of ThreeDots
         * it's job is to open/close the the Drawer and pause/play the feed video.
         */
        this.toggleDrawerMenu = () => {
            const { isDrawerOpen } = this.state;
            State.isSlidePrevented = !isDrawerOpen;
            if (!isDrawerOpen) {
                this.bubbleEvent.setGlanceMoreOpen(this.getCurrentGlanceId());
            }
            this.setState((prevState) => {
                return {
                    isAutoPlayPaused: !prevState.isDrawerOpen,
                    isDrawerOpen: !prevState.isDrawerOpen,
                };
            });
        };
        this.changeGlanceIndex = (index) => {
            const { setSlideshowIndex } = this.props;
            State.lastPageChangeSource = "AutoNext" /* AUTO */;
            setSlideshowIndex(index);
        };
        this.markBubbleStart = () => {
            /***
             * the com
             */
            const { startPosition } = this.props.bubble;
            //sending glanceID to glance webapp-SSR to update URL in the reactivation flow.
            if (!State.isSdk && isFunction(this.props.setStoryIdInPWA)) {
                this.props.setStoryIdInPWA(this.getCurrentGlanceId());
            }
            this.bubbleEvent.setBubbleShown(State.lastPageChangeSource, this.getCurrentGlanceId(), startPosition);
        };
        this.webpeekOnLoadCb = (glanceId, loaded) => {
            this.webpeekLoadedPending[glanceId] = loaded;
            const webpeekLoaded = Object.assign(Object.assign({}, this.state.webpeekLoaded), this.webpeekLoadedPending);
            webpeekLoaded[glanceId] = loaded;
            this.setState({
                webpeekLoaded,
            }, () => {
                this.webpeekLoadedPending = {};
            });
        };
        this.setAutoPlayState = (state) => {
            this.setState({
                isAutoPlayPaused: state,
            });
        };
        this.fireImpressionEvents = () => {
            this.bubbleEvent.setImpressionEnd(this.getCurrentGlanceId(), State.lastPageChangeSource);
        };
        this.resetEvents = () => {
            this.bubbleEvent.resetEvent();
        };
        this.informStateUpdatedOnInvisible = () => {
            this.fireImpressionEvents();
        };
        this.handleInteractionMode = () => {
            this.setState((prevState) => (Object.assign(Object.assign({}, prevState), { mode: prevState.mode === "FOCUS" /* FOCUS */
                    ? "NORMAL" /* NORMAL */
                    : "FOCUS" /* FOCUS */ })));
        };
        this.handleInteractionModeAfterDelay = () => {
            const { hasDelayPassed } = this.state;
            if (hasDelayPassed) {
                this.handleInteractionMode();
            }
        };
        this.startDelayTimer = () => {
            this.setState({ mode: "NORMAL" /* NORMAL */, hasDelayPassed: false });
            clearTimeout(this.timer);
            this.timer = window.setTimeout(() => {
                this.setState({ hasDelayPassed: true, mode: "FOCUS" /* FOCUS */ });
            }, INTERACTION_EXP_DELAY);
        };
        this.resetModeAndTimer = () => {
            this.setState({ mode: "NORMAL" /* NORMAL */, hasDelayPassed: false });
        };
        /***
         *  Logic to display which is the current glance card in the bubble
         */
        let currentHighlight = 0;
        if (!isEmpty(props.bubble.startPosition)) {
            const firstUnseen = props.bubble.startPosition;
            if (firstUnseen > 0) {
                currentHighlight = firstUnseen;
            }
        }
        if (State.getStoryStatus(props.index)) {
            currentHighlight = State.getStoryStatus(props.index);
        }
        this.state = {
            isAutoPlayPaused: false,
            highlightIndex: currentHighlight,
            isHolding: false,
            isDrawerOpen: false,
            youtubeLoaded: {},
            webpeekLoaded: {},
            hiddenElements: {},
            mode: "NORMAL" /* NORMAL */,
            hasDelayPassed: false,
        };
        props.bubble.glances.forEach((gl, idx) => {
            this.glanceIndexIdMap[idx] = gl.glanceId;
        });
        /***
         * initialising Analytics for bubble / glance
         */
        this.bubbleEvent = new BubbleEventFactory();
        this.bubbleEvent.createEvents(props.bubble, props.index);
    }
    componentDidMount() {
        const { index, currentGlanceIndex } = this.props;
        /***
         * As we are rendering 3 Glances at one time,
         * So all 3 glances, componentDidMount will run,
         * so we are adding this condition to run below fns only in case of 1st Glance.
         */
        if (index === currentGlanceIndex) {
            /***
             * is current glance, initialise events, adding bridges for webpeeks
             */
            this.markBubbleStart();
            this.addbridges();
            if (State.isSdk) {
                this.startDelayTimer();
            }
        }
    }
    componentWillUnmount() {
        resetHighlightBridge(this.getCurrentGlanceId());
        clearTimeout(this.timer);
    }
    componentDidUpdate(prevProps, prevState) {
        const { currentGlanceIndex, index, isVisible } = this.props;
        const { highlightIndex } = this.state;
        /**
         * below conditional block gets executed when you come back to highlights tab(home tab).
         */
        if (isVisible && !prevProps.isVisible) {
            this.resetEvents();
        }
        if (prevProps.currentGlanceIndex === prevProps.index &&
            currentGlanceIndex !== index) {
            // card is prev card now
            if (!this.tapEventFired) {
                const isVideoGlance = this.currentGlanceHasVideo();
                this.bubbleEvent.setImpressionEnd(this.getCurrentGlanceId(), State.lastPageChangeSource, isVideoGlance);
            }
            else {
                this.tapEventFired = false;
            }
        }
        /***
         * As we are rendering 3 Glances at one time,
         * So all 3 glances, componentDidMount will run,
         * so we are adding this condition to run below fns, on other rendered glances.
         */
        if (prevProps.currentGlanceIndex !== prevProps.index &&
            currentGlanceIndex === index) {
            //card is active now
            this.markBubbleStart();
            this.addbridges();
            if (State.isSdk) {
                this.startDelayTimer();
            }
        }
        /**
         * whenever we move to another glance,
         * reset mode and timer.
         */
        if (prevProps.currentGlanceIndex !== currentGlanceIndex && State.isSdk) {
            this.resetModeAndTimer();
        }
        /**
         * whenever we move to different highlights,
         * start delay timer.
         */
        if (prevState.highlightIndex !== highlightIndex && State.isSdk) {
            this.startDelayTimer();
        }
    }
    shouldComponentUpdate(nextProps, nextState) {
        const { index, currentGlanceIndex, slideshowPaused } = this.props;
        const { hiddenElements } = this.state;
        const nextIndex = nextProps.index;
        const nextCurrentIndex = nextProps.currentGlanceIndex;
        if (isInRenderThreshold(index, currentGlanceIndex) ||
            isInRenderThreshold(nextIndex, nextCurrentIndex) ||
            (slideshowPaused !== nextProps.slideshowPaused &&
                index === currentGlanceIndex) ||
            (JSON.stringify(hiddenElements) !==
                JSON.stringify(nextState.hiddenElements) &&
                index === currentGlanceIndex)) {
            return true;
        }
        return false;
    }
    static getDerivedStateFromProps(props, state) {
        if (!State.isSdk &&
            props.slideshowPaused !== state.isAutoPlayPaused &&
            props.index === props.currentGlanceIndex) {
            return {
                isAutoPlayPaused: props.slideshowPaused,
            };
        }
        return null;
    }
    // eslint-disable-next-line sonarjs/cognitive-complexity
    render() {
        var _a, _b;
        const { bubble, currentGlanceIndex, index, slideshowPaused, isVisible, experiments, } = this.props;
        const { glances } = bubble;
        const { highlightIndex, isHolding, isAutoPlayPaused, isDrawerOpen, youtubeLoaded, webpeekLoaded, hiddenElements, mode, } = this.state;
        const glance = glances[highlightIndex];
        if (!glance)
            return null;
        const source = (_a = glance.peekData) === null || _a === void 0 ? void 0 : _a.sourceName;
        const isAWebPeek = isWebpeek(glance);
        const isNativeVideo = hasNativeVideo(glance);
        const isYoutubeVideo = hasYoutubeVideo(glance);
        const hasVideo = isNativeVideo || isYoutubeVideo;
        const isFirstGlanceCard = index === 0;
        const isLastCard = highlightIndex === glances.length - 1;
        const { hasCoachMark, isOnline } = State;
        const hasCoachMarkAnimation = isFirstGlanceCard &&
            !isAWebPeek &&
            hasCoachMark &&
            isLastCard &&
            !isDrawerOpen;
        const className = [
            getSlideClassName(currentGlanceIndex, index, hasCoachMarkAnimation),
        ];
        const isCardVisible = checkCardVisible(currentGlanceIndex, index);
        const attribution = !isEmpty(glance.attribution)
            ? JSON.stringify(glance.attribution)
            : JSON.stringify({});
        const interactionExperiment = getInteractionExperiment(experiments);
        const hasExperimentResponse = experiments.status === "resolved" /* RESOLVED */ ||
            experiments.status === "rejected" /* REJECTED */;
        /**
         * If any interaction variant key is true,
         * means interaction experiment is enabled.
         */
        const enableExperiment = interactionExperiment &&
            Object.values(interactionExperiment).some((value) => value === true);
        const bubbleTitle = bubble.name || glance.bubbleTitle;
        const thumbnailUrl = bubble.imageUrl || glance.thumbnailUrl;
        const showInteractionExperiment = State.isSdk && enableExperiment && !isAWebPeek;
        return (React.createElement("section", { id: `card-${index}`, className: `${Container} ${className.join(' ')}` },
            React.createElement(Stories, { glances: glances, highlightIndex: highlightIndex, bubbleEvent: this.bubbleEvent, index: index, currentGlanceIndex: currentGlanceIndex, youtubeLoaded: youtubeLoaded, isAutoPlayPaused: isAutoPlayPaused || isDrawerOpen, slideshowPaused: slideshowPaused, isHolding: isHolding, startPendingAutoPlay: this.startPendingAutoPlay, webpeekOnLoadCb: this.webpeekOnLoadCb, webpeekLoaded: webpeekLoaded, isHomeVisible: isVisible, informStateUpdatedOnInvisible: this.informStateUpdatedOnInvisible }),
            isNativeVideo && State.fromSportsPage && !getCTA(glance) && (React.createElement(JioBanner, null)),
            isCardVisible && (React.createElement(React.Fragment, null,
                React.createElement(GlanceLogo, null),
                !isHolding && State.isSdk && React.createElement(SwitchLanguage, null),
                React.createElement("div", { className: StoryContainer },
                    React.createElement(GlanceHighlights, { highlights: glances, highlightIndex: highlightIndex, glanceIndex: currentGlanceIndex, index: index, changeGlanceIndex: this.changeGlanceIndex, changeHighlightIndex: this.setHighlightIndex, isAutoPlayPaused: isAutoPlayPaused || isHolding || isAWebPeek || !isVisible, hasAudio: hasVideo, isHolding: isHolding })),
                React.createElement("div", { className: `${BubbleContainer}${hasVideo ? ' video' : ''}` },
                    React.createElement(BubbleDetails, { hasAudio: hasVideo, isHolding: isHolding, bubbleTitle: bubbleTitle, thumbnailUrl: thumbnailUrl, source: source, isWebpeek: isAWebPeek, hiddenElements: hiddenElements[glance.glanceId] })),
                glances.length > 0 &&
                    (!isAWebPeek ||
                        (isAWebPeek && !webpeekLoaded[glance.glanceId])) && (React.createElement(Navigator, { index: index, onNavigate: this.onNavigate, onHoldGlance: this.onHoldGlance, onUnHoldGlance: this.onUnHoldGlance, onToggleMode: this.handleInteractionModeAfterDelay, isExperimentEnabled: State.isSdk && enableExperiment })),
                !isHolding && (React.createElement(React.Fragment, null,
                    !isOnline && React.createElement(OfflineBadge, null),
                    hasExperimentResponse && enableExperiment !== null ? (showInteractionExperiment ? (React.createElement(InteractionCTActions, { hiddenElements: hiddenElements[glance.glanceId], glance: glance, toggleDrawerMenu: this.toggleDrawerMenu, toggleAutoPlay: this.toggleAutoPlay, bubbleEvent: this.bubbleEvent, isWebpeek: isAWebPeek, mode: mode, interactionExperiment: interactionExperiment, onToggleMode: this.handleInteractionModeAfterDelay, index: index })) : (React.createElement(CTActions, { hiddenElements: hiddenElements[glance.glanceId], glance: glance, toggleDrawerMenu: this.toggleDrawerMenu, toggleAutoPlay: this.toggleAutoPlay, bubbleEvent: this.bubbleEvent, isWebpeek: isAWebPeek, index: index }))) : null,
                    hasCoachMark && isFirstGlanceCard && !isDrawerOpen && (React.createElement(CoachMarkContainer, { isLastStoryInCard: isLastCard, hasTapHere: highlightIndex === 0 })))),
                isYoutubeVideo &&
                    !youtubeLoaded[glance.glanceId] &&
                    isOnline &&
                    !constants.HAS_YOUTUBE_AUTOPLAY && (React.createElement("div", { className: playBtnCss, onClick: this.loadYoutube, "data-id": glance.glanceId },
                    React.createElement(PlayBtn, null))),
                isDrawerOpen && (React.createElement(ShareTray, { glanceId: glance.glanceId, shareUrl: glance === null || glance === void 0 ? void 0 : glance.shareUrl, attribution: attribution, bubbleEvent: this.bubbleEvent, closeDrawerMenu: this.closeDrawerMenu })),
                isHolding && (React.createElement("span", { className: Attribution }, (_b = glance.attribution) === null || _b === void 0 ? void 0 : _b.text))))));
    }
}
export default Glance;
//# sourceMappingURL=index.js.map